import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Eye, EyeOff } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const empty = { title: "", image: "", link: "", active: true };

export const BannersManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/banners").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (editing.id) await api.put(`/admin/banners/${editing.id}`, form);
    else await api.post("/admin/banners", form);
    toast.success("Banner salvo"); setEditing(null); load();
  };
  const remove = async (b) => { if (!window.confirm("Excluir banner?")) return; await api.delete(`/admin/banners/${b.id}`); toast.success("Excluído"); load(); };
  const toggle = async (b) => { await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active }); load(); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url })); toast.success("Imagem enviada");
    } catch { toast.error("Falha no upload"); } finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-banners">
      <PageTitle title="Banners" subtitle={`${items.length} banners`}
        action={<GoldButton onClick={() => { setForm(empty); setEditing({}); }} data-testid="banner-add"><Plus className="w-4 h-4 inline mr-1" /> Novo Banner</GoldButton>} />
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((b) => (
          <Card key={b.id} className="!p-3" data-testid={`banner-${b.id}`}>
            <div className="aspect-[21/9] rounded-xl overflow-hidden bg-white/5 mb-3">
              {b.image ? <img src={imgUrl(b.image)} alt={b.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">Sem imagem</div>}
            </div>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{b.title || "(sem título)"}</div>
                <div className="text-xs text-white/40 truncate">{b.link || "—"}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggle(b)} className="p-2 text-white/60 hover:text-[#F5C542]" title={b.active ? "Ativo" : "Inativo"}>
                  {b.active ? <Eye className="w-4 h-4 text-[#00FF41]" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => { setForm(b); setEditing(b); }} className="p-2 text-white/60 hover:text-[#F5C542]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(b)} className="p-2 text-white/60 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="col-span-full text-center py-10 text-white/40">Nenhum banner ainda.</Card>}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setEditing(null)} />
            <motion.form onSubmit={save} initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }} className="relative glass-gold w-full max-w-lg p-6" data-testid="banner-modal">
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              <h3 className="font-display text-xl font-bold text-white mb-5">{editing.id ? "Editar Banner" : "Novo Banner"}</h3>
              <div className="space-y-4">
                <Field label="Título"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="banner-title" /></Field>
                <Field label="Link (URL)"><input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className={inputCls} /></Field>
                <Field label="Imagem">
                  <div className="flex gap-2">
                    <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} />
                    <label className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl bg-white/[0.04] border border-white/10 text-white/70 cursor-pointer text-sm">
                      <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                    </label>
                  </div>
                </Field>
                {form.image && <img src={imgUrl(form.image)} alt="" className="w-full aspect-[21/9] object-cover rounded-xl border border-white/10" />}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <span className="text-sm text-white/70">Ativo</span>
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <GoldButton type="submit" className="flex-1" data-testid="banner-save">Salvar</GoldButton>
                <GhostButton type="button" onClick={() => setEditing(null)}>Cancelar</GhostButton>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
